import { type UsedSourceMetadatas } from "@do/shared-types";
import { TooltipProvider } from "@do/ui/components/tooltip";
import { motion } from "motion/react";
import { useTranslations } from "next-intl";
import { useMotionEnabled } from "../../../../hooks/use-motion-enabled";
import {
  getRevealProps,
  MESSAGE_ACTIONS_DELAY_S,
  MESSAGE_REVEAL_TIMING,
} from "../../../../lib/motion-presets";
import { useChat } from "../../../../providers/chat-provider";
import { MessageActionAiGenerated } from "./ai-generated";
import { MessageActionBackButton } from "./back-button";
import { MessageActionCopyButton } from "./copy-button";
import { MessageActionEditButton } from "./edit-button";
import { MessageActionSources } from "./sources";
import { MessageActionTTSButton } from "./tts-button";

interface MessageActionsProps {
  text: string;
  id: string;
  isResponseEditionEnabled: boolean;
  canBackToPreviousStep: boolean;
  usedSourcesMetadatas: UsedSourceMetadatas[];
  isGenerative: boolean;
  /** The message's own entrance delay: this row's beat is counted from when it starts. */
  revealDelayS?: number;
}

export const MessageActions = ({
  text,
  id,
  isResponseEditionEnabled,
  canBackToPreviousStep,
  usedSourcesMetadatas,
  isGenerative,
  revealDelayS = 0,
}: MessageActionsProps) => {
  const { chatbotParams, onEditBotResponse } = useChat();
  const t = useTranslations("do-chat-v2");
  const motionEnabled = useMotionEnabled();

  return (
    <TooltipProvider delayDuration={700} skipDelayDuration={100}>
      <motion.div
        // A fade and nothing else. These sit under a message that is itself still rising,
        // and a second thing moving there would compete with it for attention — where the
        // whole point of this row is to arrive quietly once the answer has landed.
        {...getRevealProps({
          motionEnabled,
          timing: MESSAGE_REVEAL_TIMING,
          delayS: revealDelayS + MESSAGE_ACTIONS_DELAY_S,
        })}
        id={`message-${id}-actions-container`}
        className="flex w-full flex-row items-center justify-between mt-(--message-actions-margin-top) mb-(--message-actions-margin-bottom)"
        role="group"
        aria-label={t("chat.messages.accessibility.message-actions-aria-label")}
      >
        <div className="flex flex-row items-center gap-(--message-actions-gap)">
          {canBackToPreviousStep && <MessageActionBackButton id={id} />}
          {text && chatbotParams.enableCopyMessageButton && (
            <MessageActionCopyButton text={text} id={id} />
          )}
          {text && chatbotParams.ttsEnabled && <MessageActionTTSButton text={text} id={id} />}
          {usedSourcesMetadatas.length > 0 && (
            <MessageActionSources id={id} usedSourcesMetadatas={usedSourcesMetadatas} />
          )}
          {isResponseEditionEnabled && onEditBotResponse && <MessageActionEditButton id={id} />}
        </div>
        {isGenerative && <MessageActionAiGenerated />}
      </motion.div>
    </TooltipProvider>
  );
};
